import Phaser from 'phaser';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT } from '../render/layout';

// Loads the critical UI asset set (base atlases, standalone images, park data,
// tutorial videos) while showing the already-cached splash + a progress bar.
// Per-puppy atlases load lazily in MainScene (current level) and via background
// prefetch (the rest); audio is owned by Howler (SoundBank), not Phaser.
export class PreloadScene extends Phaser.Scene {
  constructor() {
    super({ key: 'Preload' });
  }

  preload(): void {
    this.cameras.main.setBackgroundColor('#108aef');
    this.showSplashAndBar();

    const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
    this.load.setPath(`${base}assets`);

    // Base UI / shared atlases (NOT the per-puppy p{n}a{n} atlases).
    const atlases = [
      'puppies', 'puppieslg', 'eyes',
      'horsey', 'merrygoround', 'seesaw', 'parkbench',
      'settings', 'settingsbutton', 'sparklespin',
      'squaresbevel',
    ];
    for (const key of atlases) {
      this.load.atlas(key, `atlases/${key}.png`, `atlases/${key}.json`);
    }

    const images = [
      'doghouse2', 'doghousesign2', 'dogbowl2', 'iPadFrame', 'next',
      'sky6plus', 'ground6plus', 'ip6',
      'smiley', 'smileylg',
      'cloud1', 'cloud2', 'cloud3',
      'crazysquare2', 'crazysquareframe', 'crazycountcircle',
      'menupaw', 'bestsquarec',
      'bonus', 'dogbonus', 'bottombar', 'cant',
      'grassrect3', 'thunderstorm',
      'crazysquarelg2', 'crazysquarelg2wstars',
      'gotonextpark', 'replayLevel', 'startover',
      'keepplaying', 'endkeepgoing', 'doghousefull',
      'settings-play', 'settings-newgame', 'hidebottombar',
      'frame5', 'frame6',
      'megastars', 'megapuppy', 'plus100', 'plus50', 'undo', 'usecrazysquare',
      'loadscreen',
    ];
    for (const key of images) {
      this.load.image(key, `images/${key}.png`);
    }

    this.load.json('parknames', 'data/parknames.json');

    // Videos (DogTraining tutorial). Kept in the critical set because the
    // tutorial auto-opens on first launch, before background prefetch finishes
    // — and they're only ~2.2 MB total. `false` = don't auto-decode on load.
    for (const key of ['BigPuppy', 'MegaPuppy', 'ClearGrass']) {
      this.load.video(key, `video/${key}.mp4`, false);
    }
  }

  create(): void {
    this.input.once('pointerdown', () => {
      this.scene.start('Main');
    });
  }

  private showSplashAndBar(): void {
    // loadscreen was loaded by BootScene, so it's already in the texture cache.
    const lsW = LOGICAL_WIDTH;
    const lsH = lsW * (3132 / 1440);
    this.add
      .image(LOGICAL_WIDTH / 2, LOGICAL_HEIGHT / 2, 'loadscreen')
      .setOrigin(0.5)
      .setDisplaySize(lsW, lsH);

    // Splash is now on the display list — drop the first-paint HTML spinner on
    // the next frame so the splash has painted before it disappears (no flash).
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => document.getElementById('app-loader')?.remove());
    } else {
      document.getElementById('app-loader')?.remove();
    }

    const barW = LOGICAL_WIDTH * 0.6;
    const barH = 18;
    const barX = (LOGICAL_WIDTH - barW) / 2;
    const barY = LOGICAL_HEIGHT * 0.8;

    const frame = this.add.graphics();
    frame.lineStyle(3, 0xffffff, 0.9).strokeRoundedRect(barX, barY, barW, barH, 9);

    const fill = this.add.graphics();
    this.load.on('progress', (value: number) => {
      fill.clear();
      fill.fillStyle(0xffffff, 0.95).fillRoundedRect(barX + 2, barY + 2, (barW - 4) * value, barH - 4, 7);
    });
    this.load.once('complete', () => {
      fill.destroy();
      frame.destroy();
    });
  }
}
